// src/utils/payrollPeriod.js

const TIMEZONE = 'Europe/London';
const PAYROLL_START_DAY = 26;
const PAYROLL_END_DAY = 25;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                     "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Get the UTC timestamp of the start of a local day in a given timezone.
 * Uses Intl.DateTimeFormat to read the offset on that exact date.
 */
function startOfDayInZone(year, month, day, timeZone) {
  // Create a date in the local zone at midnight
  const localDate = new Date(year, month, day, 0, 0, 0);
  // Use Intl to get the offset in minutes
  const parts = Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset'
  }).formatToParts(localDate);
  const offsetPart = parts.find(p => p.type === 'timeZoneName');
  // offsetPart.value looks like "GMT+01:00" or "GMT-05:00"
  const offsetMatch = offsetPart?.value?.match(/GMT([+-]\d{2}):(\d{2})/);
  if (!offsetMatch) {
    // fallback: assume UTC if not parseable
    return Date.UTC(year, month, day, 0, 0, 0);
  }
  const offsetMinutes = parseInt(offsetMatch[1]) * 60 + parseInt(offsetMatch[2]);
  // UTC = local midnight - offset (since offset is positive for ahead of UTC)
  return Date.UTC(year, month, day) - offsetMinutes * 60 * 1000;
}

/**
 * Get the UTC timestamp of the end of a local day (23:59:59.999) in a timezone.
 */
function endOfDayInZone(year, month, day, timeZone) {
  const localDate = new Date(year, month, day, 23, 59, 59, 999);
  const parts = Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset'
  }).formatToParts(localDate);
  const offsetPart = parts.find(p => p.type === 'timeZoneName');
  const offsetMatch = offsetPart?.value?.match(/GMT([+-]\d{2}):(\d{2})/);
  if (!offsetMatch) {
    return Date.UTC(year, month, day, 23, 59, 59, 999);
  }
  const offsetMinutes = parseInt(offsetMatch[1]) * 60 + parseInt(offsetMatch[2]);
  return Date.UTC(year, month, day, 23, 59, 59, 999) - offsetMinutes * 60 * 1000;
}

/**
 * Get the payroll period that contains a given date (default: now).
 * Returns: { startDate: Date, endDate: Date, label: string }
 */
export function getPayrollPeriod(date = new Date()) {
  const day = date.getDate();
  let startMonth = date.getMonth();
  let startYear = date.getFullYear();

  if (day < PAYROLL_START_DAY) {
    startMonth -= 1;
    if (startMonth < 0) {
      startMonth = 11;
      startYear -= 1;
    }
  }

  // start of the 26th in London time -> UTC
  const startDate = new Date(startOfDayInZone(startYear, startMonth, PAYROLL_START_DAY, TIMEZONE));

  let endMonth = startMonth + 1;
  let endYear = startYear;
  if (endMonth > 11) {
    endMonth = 0;
    endYear += 1;
  }
  // end of the 25th in London time -> UTC
  const endDate = new Date(endOfDayInZone(endYear, endMonth, PAYROLL_END_DAY, TIMEZONE));

  const label = `${PAYROLL_START_DAY} ${MONTH_NAMES[startMonth]} ${startYear} – ${PAYROLL_END_DAY} ${MONTH_NAMES[endMonth]} ${endYear}`;

  return { startDate, endDate, label };
}

/**
 * Get a list of recent payroll periods.
 */
export function getRecentPayrollPeriods(monthsBack = 6, monthsForward = 2) {
  const periods = [];
  const now = new Date();
  for (let i = -monthsBack; i <= monthsForward; i++) {
    const target = new Date(now.getFullYear(), now.getMonth() + i, 15);
    periods.push(getPayrollPeriod(target));
  }
  return periods.filter((period, index, self) =>
    index === self.findIndex(p => p.label === period.label)
  );
}